import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { socialMediaList } from "../../utils/util";
import { SocialNetwork } from "Types";
import KSocialIcon from "./KSocialIcons";
import DSBox from "../../views/system-design/DSBox";
import DSTextField from "../../views/system-design/DSTextField";
import DSPopover from "../../views/system-design/DSPopover";
import DSPrimaryButton from "../../views/system-design/DSPrimaryButton";
import DSIconButton from "../../views/system-design/DSIconButton";

const EditableSocialNetwork = (props: {
  net: SocialNetwork;
  onChange: (net: SocialNetwork) => void;
  onRemove: () => void;
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState("");

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          width: "fit-content",
          margin: "5px",
          padding: 0,
          borderRadius: "20px",
          border: open ? "1px solid" : 0,
          borderColor: "divider",
          bgcolor: open ? "background.paper" : "transparent",
        }}
      >
        <KSocialIcon
          network={props.net.network.toLowerCase()}
          title={props.net.network}
          style={{ height: 35, width: 35 }}
          onClick={() => setOpen(!open)}
        ></KSocialIcon>
        {open && edit === "" && (
          <DSBox sx={{ display: "flex", alignItems: "center" }}>
            <a href={props.net.link}>{props.net.link}</a>
            <EditIcon
              fontSize="small"
              sx={{ margin: "5px", cursor: "pointer" }}
              titleAccess="Edit"
              onClick={() => setEdit(props.net.link)}
            />
          </DSBox>
        )}
        {open && edit && (
          <DSBox sx={{ display: "flex", alignItems: "center" }}>
            <DSTextField
              autoFocus
              size="small"
              defaultValue={props.net.link}
              onChange={(e) => setEdit(e.target.value)}
            />
            <SaveIcon
              fontSize="small"
              sx={{ margin: "5px", cursor: "pointer" }}
              titleAccess="Save"
              onClick={() => {
                props.onChange({ network: props.net.network, link: edit });
                setEdit("");
              }}
            />
          </DSBox>
        )}
      </DSBox>
      <DSBox
        title="Remove"
        onClick={props.onRemove}
        sx={{
          position: "relative",
          top: "5px",
          left: "-15px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "fit-content",
          height: "fit-content",
          borderRadius: "50%",
          color: "common.black",
          bgcolor: "error.main",
          cursor: "pointer",
        }}
      >
        <CloseIcon sx={{ fontSize: "0.8rem" }} />
      </DSBox>
    </>
  );
};
const SocialNetworkEdit = (props: {
  list: Array<SocialNetwork>;
  onChange: (list: Array<SocialNetwork>) => void;
  networks?: Array<string>;
}): JSX.Element => {
  const [list, setList] = useState(props.list);
  const options = props.networks ?? socialMediaList;
  const isAllowed = (network: string) =>
    !props.networks ||
    props.networks.some((n) => n.toLowerCase() === network.toLowerCase());
  const [network, setNetwork] = useState("");
  const [link, setLink] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <DSBox sx={{ display: "flex" }}>
        {list.map((n, i) =>
          isAllowed(n.network) ? (
            <EditableSocialNetwork
              key={i}
              net={n}
              onChange={(net) => {
                list[i] = net;
                setList([...list]);
                props.onChange(list);
              }}
              onRemove={() => {
                list.splice(i, 1);
                setList([...list]);
                props.onChange(list);
              }}
            />
          ) : null,
        )}
      </DSBox>
      {network === "" ? (
        <>
          {list.length === 0 ? (
            <DSPrimaryButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              add social network
            </DSPrimaryButton>
          ) : (
            <DSIconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                height: 35,
                width: 35,
                bgcolor: "success.main",
                color: "common.black",
                "&:hover": { bgcolor: "success.dark" },
              }}
            >
              <AddIcon fontSize="small" />
            </DSIconButton>
          )}
          <DSPopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <DSBox sx={{ width: 250, padding: "10px" }}>
              {options.map((n) => (
                <KSocialIcon
                  key={n}
                  network={n.toLowerCase()}
                  title={n}
                  style={{ height: 35, width: 35, margin: 5 }}
                  onClick={() => {
                    setNetwork(n);
                    setAnchorEl(null);
                  }}
                ></KSocialIcon>
              ))}
            </DSBox>
          </DSPopover>
        </>
      ) : (
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            width: "fit-content",
            margin: "5px",
            padding: 0,
            borderRadius: "20px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <KSocialIcon
            network={network.toLowerCase()}
            title={network}
            style={{ height: 35, width: 35 }}
          ></KSocialIcon>
          <DSTextField
            autoFocus
            size="small"
            onChange={(e) => setLink(e.target.value)}
          />
          <SaveIcon
            fontSize="small"
            sx={{ margin: "5px", cursor: "pointer" }}
            titleAccess="Save"
            onClick={() => {
              if (link === "") {
                setNetwork("");
                return;
              }
              list.push({ network: network, link: link });
              setList([...list]);
              props.onChange(list);
              setNetwork("");
              setLink("");
            }}
          />
        </DSBox>
      )}
    </>
  );
};

export default SocialNetworkEdit;
